import React, { useState, useCallback, useRef, useMemo } from 'react';

import classNames from 'classnames';
import { useMutation, useQuery, useQueryCache } from 'react-query';
import { Toast } from 'src/common/components';
import { Icon } from 'src/common/components/Icon';
import { OrbitIcons } from 'src/common/constants.';

import { fetchPlaybackAudios, createPlaybackAudio, deletePlaybackAudio } from '../api';
import type { PlaybackAudio, PlaybackAudioType } from '../api';

interface PlaybackAudioManagerProps {
  accountId: number;
}

const AUDIO_TYPE_LABELS: Record<PlaybackAudioType, string> = {
  greeting: 'Saludo',
  hold_music: 'Música de espera',
  announce: 'Anuncio',
  message: 'Mensaje',
  ivr_prompt: 'Prompt IVR',
};

const AUDIO_TYPE_ICONS: Record<PlaybackAudioType, string> = {
  greeting: OrbitIcons.Phone,
  hold_music: OrbitIcons.Clock,
  announce: 'volume-2',
  message: OrbitIcons.Mail,
  ivr_prompt: OrbitIcons.PhoneIncoming,
};

export const PlaybackAudioManager: React.FC<PlaybackAudioManagerProps> = ({ accountId }) => {
  const queryCache = useQueryCache();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState<{
    name: string;
    type: PlaybackAudioType;
    file: File | null;
  }>({
    name: '',
    type: 'greeting',
    file: null,
  });
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [playingAudioId, setPlayingAudioId] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const { data: audios = [], isLoading } = useQuery(
    ['playback-audios', accountId],
    () => fetchPlaybackAudios(accountId),
    { refetchOnWindowFocus: false }
  );

  const [removeAudio] = useMutation((id: number) => deletePlaybackAudio(id), {
    onSuccess: () => {
      Toast.success('Audio eliminado exitosamente');
      queryCache.invalidateQueries(['playback-audios', accountId]);
    },
    onError: () => {
      Toast.error('Error al eliminar el audio');
    },
  });

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file) {
      setUploadForm((prev) => ({
        ...prev,
        file,
        name: prev.name || file.name.replace(/\.[^/.]+$/, ''),
      }));
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (!uploadForm.file || !uploadForm.name.trim()) return;

    setIsUploading(true);
    try {
      await createPlaybackAudio(accountId, uploadForm.name, uploadForm.type, uploadForm.file);
      Toast.success('Audio subido exitosamente');
      queryCache.invalidateQueries(['playback-audios', accountId]);
      setUploadForm({ name: '', type: 'greeting', file: null });
      setShowUploadForm(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch {
      Toast.error('Error al subir el audio');
    } finally {
      setIsUploading(false);
    }
  }, [uploadForm, accountId, queryCache]);

  const handlePlayPause = useCallback(
    (audio: PlaybackAudio) => {
      if (playingAudioId === audio.id) {
        audioRef.current?.pause();
        setPlayingAudioId(null);
      } else {
        if (audioRef.current) {
          audioRef.current.pause();
        }
        audioRef.current = new Audio(audio.public_url);
        audioRef.current.onended = () => setPlayingAudioId(null);
        audioRef.current.play().catch(() => {
          Toast.error('No se pudo reproducir el audio');
          setPlayingAudioId(null);
        });
        setPlayingAudioId(audio.id);
      }
    },
    [playingAudioId]
  );

  const groupedAudios = useMemo(() => {
    const result: Partial<Record<PlaybackAudioType, PlaybackAudio[]>> = {};
    audios.forEach((audio) => {
      const type = audio.playback_type;
      if (!result[type]) {
        result[type] = [];
      }
      result[type]!.push(audio);
    });
    return result as Record<PlaybackAudioType, PlaybackAudio[]>;
  }, [audios]);

  const isFormValid = uploadForm.name.trim().length > 0 && uploadForm.file !== null;

  return (
    <div className="p-4">
      {isLoading && <p className="text-sm text-gray-2.0">Cargando audios...</p>}

      {!isLoading && !showUploadForm && (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-2.0">
              {audios.length === 0
                ? 'No hay audios configurados'
                : `${audios.length} audio(s) configurado(s)`}
            </p>
            <button
              type="button"
              onClick={() => setShowUploadForm(true)}
              className="btn btn-secondary btn-sm flex items-center gap-1"
            >
              <Icon name={OrbitIcons.PlusCircle} size={14} />
              Subir audio
            </button>
          </div>

          {Object.entries(groupedAudios).map(([type, typeAudios]) => (
            <div key={type} className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon
                  name={AUDIO_TYPE_ICONS[type as PlaybackAudioType]}
                  size={14}
                  className="text-primary-2.0"
                />
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-2.0">
                  {AUDIO_TYPE_LABELS[type as PlaybackAudioType]} ({typeAudios.length})
                </p>
              </div>
              <div className="space-y-1">
                {typeAudios.map((audio) => (
                  <div
                    key={audio.id}
                    className="flex items-center justify-between rounded border border-white-2.0 bg-white-1.0 px-3 py-2"
                  >
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => handlePlayPause(audio)}
                        className={classNames(
                          'flex h-8 w-8 items-center justify-center rounded-full',
                          playingAudioId === audio.id
                            ? 'bg-primary-2.0 text-white'
                            : 'bg-white-2.0 text-gray-2.0 hover:bg-primary-0.0 hover:text-primary-2.0'
                        )}
                      >
                        <Icon
                          name={
                            playingAudioId === audio.id ? OrbitIcons.X : OrbitIcons.ChevronRight
                          }
                          size={14}
                        />
                      </button>
                      <div>
                        <p className="text-sm font-medium text-black-1.0">{audio.name}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm('¿Eliminar este audio?')) {
                          removeAudio(audio.id);
                        }
                      }}
                      className="p-1 text-gray-2.0 hover:text-red-500"
                    >
                      <Icon name={OrbitIcons.Trash} size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {audios.length === 0 && (
            <div className="rounded border border-dashed border-white-2.0 py-8 text-center">
              <Icon name="volume-2" size={32} className="mx-auto mb-2 text-gray-1.0" />
              <p className="text-sm text-gray-2.0">No hay audios configurados</p>
              <p className="text-xs text-gray-1.0">
                Sube tu primer audio para usarlo en los módulos
              </p>
            </div>
          )}
        </>
      )}

      {showUploadForm && (
        <div className="rounded border border-white-2.0 bg-white-0.0 p-4">
          <h4 className="mb-4 text-sm font-bold text-black-1.0">Subir nuevo audio</h4>

          <div className="mb-4">
            <label className="mb-1 block text-xs font-medium text-gray-3.0">
              Nombre del audio<span className="ml-0.5 text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Ej: Saludo de bienvenida"
              value={uploadForm.name}
              onChange={(e) => setUploadForm((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full rounded border border-white-2.0 px-3 py-2 text-sm"
            />
          </div>

          <div className="mb-4">
            <label className="mb-1 block text-xs font-medium text-gray-3.0">Tipo de audio</label>
            <select
              value={uploadForm.type}
              onChange={(e) =>
                setUploadForm((prev) => ({ ...prev, type: e.target.value as PlaybackAudioType }))
              }
              className="w-full rounded border border-white-2.0 px-3 py-2 text-sm"
            >
              {Object.entries(AUDIO_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="mb-1 block text-xs font-medium text-gray-3.0">
              Archivo de audio<span className="ml-0.5 text-red-500">*</span>
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              onChange={handleFileSelect}
              className="w-full rounded border border-white-2.0 px-3 py-2 text-sm file:mr-4 file:rounded file:border-0 file:bg-primary-0.0 file:px-3 file:py-1 file:text-xs file:font-semibold file:text-primary-2.0"
            />
            {uploadForm.file && (
              <p className="mt-1 text-xs text-gray-2.0">
                Archivo seleccionado: {uploadForm.file.name}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowUploadForm(false);
                setUploadForm({ name: '', type: 'greeting', file: null });
              }}
              className="btn btn-ghost btn-sm"
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={!isFormValid || isUploading}
              onClick={() => void handleUpload()}
              className="btn btn-secondary btn-sm"
            >
              {isUploading ? 'Subiendo...' : 'Subir audio'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
